import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const UtilizationPanel = withListOverflow(UtilizationListOwner);
// src/features/locations/UtilizationPanel.jsx
//
// UTIL-S2: Utilization (badges) body panel.
// The KPI hero strip is rendered by UtilizationTab (the tab orchestrator)
// ABOVE this component; UtilizationPanel owns only the panel body:
//
//   1. Month navigator — shared prev/next chevrons + month label
//   2. Historical line chart — Attendance % + Seat Utilization % over time
//   3. Seat assignment waffle + selected-month detail bars (two-column)
//   4. Neighborhood / team pressure table — SEED demo data
//   5. Busiest Days section — PROPOSED badge (no daily data yet)
//
// Exports (all are pure helpers, tested directly):
//   computeMetrics(rows, capacity, headcount) — derives pct / ratio / surplus
//   computeUtilScore(rows, capacity)          — two-component util scoring
//   computePeakMarkers(series)                — argmax index per series
//   monthLabel(dateStr)                       — ISO date → "MMM 'YY"
//   mean(arr)                                 — numeric mean or null

import React, { useState, useRef, useEffect } from "react";
import { T } from "../../app/tokens.js";
import { ViewScaffold } from "../../components/ViewScaffold.jsx";

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun",
                     "Jul","Aug","Sep","Oct","Nov","Dec"];

/**
 * Convert a month date value from the server to a short display label.
 * PostgreSQL DATE columns arrive as ISO timestamps (e.g. "2026-01-01T00:00:00.000Z").
 * Slice the YYYY-MM-DD prefix before parsing to avoid timezone-shift bugs.
 * Returns "—" for null/empty/invalid input.
 */
export function monthLabel(month) {
  if (!month) return "—";
  const ymd = String(month).slice(0, 10);
  const [y, m] = ymd.split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) return "—";
  const d = new Date(y, m - 1, 1);
  if (isNaN(d.getTime())) return "—";
  const mon = MONTH_NAMES[m - 1] ?? "?";
  return `${mon} '${String(y).slice(2)}`;
}

/** Mean of an array of numbers. Returns null for empty arrays. */
export function mean(arr) {
  if (!arr || arr.length === 0) return null;
  return arr.reduce((s, v) => s + v, 0) / arr.length;
}

/**
 * Compute all derived utilization metrics from raw rows, capacity, and headcount.
 * Exported for unit tests — no rendering side effects.
 *
 * Sharing ratio = headcount / capacity (people per seat).
 *   < 1 = surplus seats, > 1 = hot-desking pressure.
 */
export function computeMetrics(rows, capacity, headcount) {
  const hasRows = rows.length > 0;
  const hasCap  = capacity > 0;
  const hcTotal = headcount?.totalCount ?? 0;
  const hasHc   = headcount != null && hcTotal > 0;

  const peakMax = hasRows
    ? Math.max(...rows.map(r => r.peakDailyUniqueBadges ?? 0))
    : null;
  const avgMean = hasRows
    ? mean(rows.map(r => Number(r.avgDailyUniqueBadges ?? 0)))
    : null;

  const peakUtilPct =
    hasCap && peakMax != null ? Math.round((peakMax / capacity) * 100) : null;
  const avgUtilPct =
    hasCap && avgMean != null ? Math.round((avgMean / capacity) * 100) : null;

  const sharingRatio =
    hasCap && hasHc ? Math.round((hcTotal / capacity) * 100) / 100 : null;
  const surplus = hasCap && hasHc ? capacity - hcTotal : null;

  return { peakMax, avgMean, peakUtilPct, avgUtilPct, sharingRatio, surplus };
}

/**
 * computeUtilScore(rows, capacity)
 *
 * Two-component util sub-score (mirrors server scoreUtilDemand).
 * Returns null when there is no data.
 *
 * Component 1 — Avg-day utilization (65%):
 *   0 → 85% : linear  (ratio / 0.85) × 100
 *   85–100%  : plateau 100
 *   > 100%   : max(70, 100 − (ratio − 1) × 100)
 *
 * Component 2 — Peak headroom (35%):
 *   0 → 80% : linear  (ratio / 0.80) × 100
 *   80–100%  : plateau 100
 *   > 100%   : max(70, 100 − (ratio − 1) × 100)
 *
 * Bands: ≥80 Optimal · 55–79 Fair · <55 Poor
 */
export function computeUtilScore(rows, capacity) {
  if (!rows || rows.length === 0 || !capacity || capacity <= 0) return null;

  const recent = rows.slice(-3);

  const avgMeanVal = recent.reduce((s, r) => s + (parseFloat(r.avgDailyUniqueBadges) || 0), 0) / recent.length;
  const peakMaxVal = Math.max(...recent.map(r => parseFloat(r.peakDailyUniqueBadges) || 0));

  const avgRatio  = avgMeanVal / capacity;
  const peakRatio = peakMaxVal / capacity;

  let avgScore;
  if (avgRatio <= 0.85)     avgScore = (avgRatio / 0.85) * 100;
  else if (avgRatio <= 1.0) avgScore = 100;
  else                       avgScore = Math.max(70, 100 - (avgRatio - 1.0) * 100);

  let peakScore;
  if (peakRatio <= 0.80)     peakScore = (peakRatio / 0.80) * 100;
  else if (peakRatio <= 1.0) peakScore = 100;
  else                        peakScore = Math.max(70, 100 - (peakRatio - 1.0) * 100);

  const subScore = Math.round((0.65 * avgScore + 0.35 * peakScore) * 10) / 10;

  let band, bandColor;
  if (subScore >= 80)      { band = "Optimal"; bandColor = T.greenDeep; }
  else if (subScore >= 55) { band = "Fair";    bandColor = T.ragAmber; }
  else                      { band = "Poor";    bandColor = T.ragRed;   }

  const avgInputPct  = Math.round(avgRatio  * 100);
  const peakInputPct = Math.round(peakRatio * 100);

  return {
    subScore,
    band,
    bandColor,
    breakdown: [
      {
        component:    "Avg-day utilization",
        description:  `${avgInputPct}% of seats · ideal ~70-85%`,
        weight:       65,
        inputPct:     avgInputPct,
        subScore:     Math.round(avgScore  * 10) / 10,
        contribution: Math.round(0.65 * avgScore  * 10) / 10,
      },
      {
        component:    "Peak headroom",
        description:  `${peakInputPct}% peak · ideal 80-95%`,
        weight:       35,
        inputPct:     peakInputPct,
        subScore:     Math.round(peakScore * 10) / 10,
        contribution: Math.round(0.35 * peakScore * 10) / 10,
      },
    ],
  };
}

/**
 * computePeakMarkers(series)
 *
 * Returns the index of the maximum value in the series array.
 * Returns null for empty/all-zero arrays.
 * Exported for unit tests.
 */
export function computePeakMarkers(series) {
  if (!series || series.length === 0) return null;
  let maxIdx = 0;
  for (let i = 1; i < series.length; i++) {
    if (series[i] > series[maxIdx]) maxIdx = i;
  }
  return maxIdx;
}

// ─── Seed data for team pressure table ───────────────────────────────────────
//
// SEED — demo data. Per-team breakdown is not yet in the API.
// Values vary by month index (modular deltas) so toggling months is visually
// meaningful. Replace with real API fields when available.

const TEAM_PRESSURE_BASE = [
  { team: "Engineering", headcount: 42, baseAssigned: 38, baseUtil: 72 },
  { team: "Product",     headcount: 18, baseAssigned: 16, baseUtil: 68 },
  { team: "Design",      headcount: 12, baseAssigned: 10, baseUtil: 58 },
  { team: "Operations",  headcount: 24, baseAssigned: 20, baseUtil: 81 },
  { team: "Sales",       headcount: 30, baseAssigned: 22, baseUtil: 63 },
  { team: "G&A",         headcount: 15, baseAssigned: 14, baseUtil: 55 },
];

// Month-offset deltas (cycles through these as the selected month changes)
const MONTH_DELTAS = [
  {  assignedDelta:  0, utilDelta:  0 },
  {  assignedDelta: -2, utilDelta: -5 },
  {  assignedDelta:  1, utilDelta:  4 },
  {  assignedDelta: -1, utilDelta:  7 },
  {  assignedDelta:  2, utilDelta: -3 },
  {  assignedDelta: -3, utilDelta:  9 },
];

/** Derive seeded team rows for a given month index (0-based). */
export function getTeamPressureRows(monthIdx) {
  const delta = MONTH_DELTAS[monthIdx % MONTH_DELTAS.length];
  return TEAM_PRESSURE_BASE.map(t => {
    const assigned = Math.max(1, Math.min(t.headcount, t.baseAssigned + delta.assignedDelta));
    const utilPct  = Math.max(10, Math.min(100, t.baseUtil + delta.utilDelta));
    let pressure, pressureColor, pressureBg;
    if (utilPct >= 85)      { pressure = "High";   pressureColor = T.ragRed;    pressureBg = T.ragRedBg;    }
    else if (utilPct >= 65) { pressure = "Medium"; pressureColor = T.ragAmber;  pressureBg = T.ragAmberBg; }
    else                    { pressure = "Low";    pressureColor = T.greenDeep; pressureBg = T.ragGreenBg; }
    return { team: t.team, headcount: t.headcount, assigned, utilPct, pressure, pressureColor, pressureBg };
  });
}

// ─── Shared style fragments ───────────────────────────────────────────────────

const EYEBROW = {
  fontSize: T.fsXs,
  fontWeight: T.fwBold,
  color: T.mid,
  textTransform: "uppercase",
  letterSpacing: 0.4,
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function SourceBadge({ text }) {
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      background: T.blueSoft,
      color: T.blue,
      borderRadius: 4,
      fontSize: T.fsXs,
      fontWeight: T.fwBold,
      padding: "2px 7px",
    }}>
      {text}
    </span>
  );
}

function ProposedBadge() {
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      background: T.lightGray,
      color: T.mid,
      borderRadius: 4,
      fontSize: T.fsXs,
      fontWeight: T.fwBold,
      padding: "2px 6px",
      letterSpacing: 0.3,
      textTransform: "uppercase",
    }}>
      PROPOSED
    </span>
  );
}

function SeedBadge() {
  return (
    <span
      data-testid="seed-badge"
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: T.utilAmber,
        color: T.amberDark,
        borderRadius: 4,
        fontSize: T.fsXs,
        fontWeight: T.fwBold,
        padding: "2px 6px",
        letterSpacing: 0.3,
        textTransform: "uppercase",
        border: `1px solid ${T.utilAmberBorder}`,
      }}
    >
      SEED - demo data
    </span>
  );
}

/**
 * MonthBarChart — div-based bar chart for monthly avg badges.
 * Click a bar (or its label) to update selectedIdx.
 * Shows: avg fill (histfill/blue when selected), gold peak dot, dashed capacity line.
 */
function MonthBarChart({ rows, capacity, selectedIdx, onSelect }) {
  if (!rows || rows.length === 0) return null;

  const avgs  = rows.map(r => parseFloat(r.avgDailyUniqueBadges)  || 0);
  const peaks = rows.map(r => parseFloat(r.peakDailyUniqueBadges) || 0);
  const maxVal = Math.max(...peaks, capacity ?? 0) * 1.05 || 1;
  const capLinePct = capacity ? (capacity / maxVal) * 100 : null;
  const CHART_H = 96;

  return (
    <div>
      {/* Bar area */}
      <div style={{ position: "relative", height: CHART_H, display: "flex", alignItems: "flex-end", gap: 3 }}>
        {/* Capacity dashed line */}
        {capLinePct != null && (
          <div style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: `${capLinePct}%`,
            height: 0,
            borderTop: `1.5px dashed ${T.borderDark}`,
            pointerEvents: "none",
            zIndex: 1,
          }} data-testid="capacity-line" />
        )}

        {rows.map((row, i) => {
          const avgH  = maxVal > 0 ? (avgs[i]  / maxVal) * 100 : 0;
          const peakH = maxVal > 0 ? (peaks[i] / maxVal) * 100 : 0;
          const isSelected = i === selectedIdx;
          return (
            <div
              key={i}
              data-testid={`month-bar-${i}`}
              onClick={() => onSelect(i)}
              title={monthLabel(row.month)}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-end",
                height: "100%",
                cursor: "pointer",
                position: "relative",
              }}
            >
              <div style={{
                width: "100%",
                height: `${Math.max(avgH, 1)}%`,
                background: isSelected ? T.blue : T.histfill,
                borderRadius: "3px 3px 0 0",
                transition: "background 0.15s",
              }} />
              {/* Peak dot */}
              <div style={{
                position: "absolute",
                bottom: `${peakH}%`,
                left: "50%",
                transform: "translate(-50%, 50%)",
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: T.utilGold,
                border: `1.5px solid ${T.card}`,
                zIndex: 2,
              }} />
            </div>
          );
        })}
      </div>

      {/* Month labels row */}
      <div style={{ display: "flex", gap: 3, marginTop: 4 }}>
        {rows.map((row, i) => (
          <div
            key={i}
            onClick={() => onSelect(i)}
            style={{
              flex: 1,
              textAlign: "center",
              fontSize: T.fsXxxs,
              color: i === selectedIdx ? T.blue : T.mid,
              fontWeight: i === selectedIdx ? T.fwBold : T.fwRegular,
              cursor: "pointer",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {monthLabel(row.month)}
          </div>
        ))}
      </div>

      {/* Avg / Peak / Capacity legend */}
      <ViewScaffold
        testid="usage-legend"
        surface="neutral"
        legendItems={[
          { label: "Avg",             indicator: { type: "line",   color: T.histfill,  dashed: false } },
          { label: "Peak",            indicator: {                  color: T.utilGold               } },
          { label: "Capacity (seats)",indicator: { type: "line",   color: T.borderDark, dashed: true } },
        ]}
      />
    </div>
  );
}

/**
 * WaffleChart — grid of colored cells representing seat assignment.
 * Each cell ≈ 10 seats.
 * Two states only: blue (assigned/in-use) · gray (open).
 * Purple "peak"/max-density third state removed per UTIL-ANALYTICAL-S2.
 */
function WaffleChart({ installed, assigned }) {
  if (!installed || installed <= 0) return null;

  const SEATS_PER_CELL = 10;
  const COLS = 12;

  const installedCells = Math.ceil(installed / SEATS_PER_CELL);
  const assignedCells  = assigned != null
    ? Math.min(Math.round(assigned / SEATS_PER_CELL), installedCells)
    : 0;
  const openCells = Math.max(0, installedCells - assignedCells);

  const cells = [
    ...Array(assignedCells).fill(T.blue),
    ...Array(openCells).fill(T.utilOpen),
  ];

  return (
    <div>
      <div style={{
        display: "grid",
        gridTemplateColumns: `repeat(${COLS}, 1fr)`,
        gap: 3,
        marginBottom: 10,
      }} data-testid="waffle-grid">
        {cells.map((color, i) => (
          <div key={i} style={{ aspectRatio: "1", background: color, borderRadius: 2 }} />
        ))}
      </div>
      <ViewScaffold
        testid="waffle-legend"
        surface="neutral"
        legendItems={[
          { label: `${assignedCells * SEATS_PER_CELL} assigned`, indicator: { color: T.blue     } },
          { label: `${openCells * SEATS_PER_CELL} open`,         indicator: { color: T.utilOpen } },
        ]}
      />
    </div>
  );
}

/**
 * HorizBar — single horizontal fill bar.
 */
function HorizBar({ fillPct, fillColor, height = 10 }) {
  const w = Math.min(100, Math.max(0, fillPct ?? 0));
  return (
    <div style={{ height, borderRadius: height / 2, background: T.border, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${w}%`, background: fillColor ?? T.blue, borderRadius: height / 2 }} />
    </div>
  );
}

/**
 * MonthDetailBars — right panel for the selected month.
 * Shows labeled % bars for: seat assignment, avg util, peak util.
 * Plus raw badge counts below each util bar.
 */
function MonthDetailBars({ row, capacity, headcount }) {
  if (!row || !capacity || capacity <= 0) return null;

  const avg  = parseFloat(row.avgDailyUniqueBadges)  || 0;
  const peak = parseFloat(row.peakDailyUniqueBadges) || 0;
  const hc   = headcount?.totalCount ?? null;

  const avgPct    = Math.min(100, Math.round((avg  / capacity) * 100));
  const peakPct   = Math.min(100, Math.round((peak / capacity) * 100));
  const assignPct = hc != null ? Math.min(100, Math.round((hc / capacity) * 100)) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Seat assignment */}
      {assignPct != null && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
            <span style={EYEBROW}>Seat Assignment</span>
            <span style={{ fontSize: T.fsXs, color: T.dark, fontWeight: T.fwSemibold }}>{assignPct}%</span>
          </div>
          <HorizBar fillPct={assignPct} fillColor={T.blue} height={8} />
          <div style={{ fontSize: T.fsXxxs, color: T.mid, marginTop: 3 }}>{hc} of {capacity} seats assigned</div>
        </div>
      )}

      {/* Avg seat utilization */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
          <span style={EYEBROW}>Avg Utilization</span>
          <span style={{ fontSize: T.fsXs, color: T.dark, fontWeight: T.fwSemibold }}>{avgPct}%</span>
        </div>
        <HorizBar fillPct={avgPct} fillColor={T.histfill} height={8} />
        <div style={{ fontSize: T.fsXxxs, color: T.mid, marginTop: 3 }}>{Math.round(avg)} avg daily badges</div>
      </div>

      {/* Peak seat utilization */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
          <span style={EYEBROW}>Peak Utilization</span>
          <span style={{ fontSize: T.fsXs, color: T.utilGold, fontWeight: T.fwSemibold }}>{peakPct}%</span>
        </div>
        <HorizBar fillPct={peakPct} fillColor={T.utilGold} height={8} />
        <div style={{ fontSize: T.fsXxxs, color: T.mid, marginTop: 3 }}>{Math.round(peak)} peak daily badges</div>
      </div>
    </div>
  );
}

// ─── SVG line chart constants ─────────────────────────────────────────────────
const CHART_W  = 600;
const CHART_H_SVG = 160;
const PAD_LEFT  = 36;
const PAD_RIGHT = 20;
const PAD_TOP   = 16;
const PAD_BOT   = 28;
const PLOT_H = CHART_H_SVG - PAD_TOP - PAD_BOT;

// Map a value 0–100 to an SVG Y coordinate (0 at top, so invert)
function svgY(pct) {
  return PAD_TOP + PLOT_H - (Math.min(100, Math.max(0, pct)) / 100) * PLOT_H;
}

/**
 * HistoricalUtilChart — SVG line chart showing two series over visible months.
 *
 * Series 1: Attendance % = avg badges / capacity (blue)
 * Series 2: Seat Utilization % = peak badges / capacity (gold)
 *
 * Peak markers: one dot per series at the month with the highest value (argmax),
 * labeled with the short month abbreviation.
 *
 * A month-range window (prev/next) allows navigating when > 12 months of data
 * are available. Default shows all months (up to 12).
 */
function HistoricalUtilChart({ rows, capacity }) {
  const WINDOW_SIZE = 12;
  const [windowStart, setWindowStart] = useState(0);

  // ResizeObserver: track the container's actual pixel width so the SVG
  // viewBox matches the rendered width exactly (scale factor = 1:1).
  // At 1:1, fontSize values in SVG <text> elements resolve to true CSS px.
  const wrapperRef = useRef(null);
  const [chartW, setChartW] = useState(CHART_W);
  const plotW = chartW - PAD_LEFT - PAD_RIGHT;

  // Local x-mapper that uses the dynamic plotW instead of the module constant.
  const svgXFn = (i, n) => {
    if (n <= 1) return PAD_LEFT + plotW / 2;
    return PAD_LEFT + (i / (n - 1)) * plotW;
  };

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setChartW(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!rows || rows.length === 0 || !capacity || capacity <= 0) {
    return (
      <div style={{
        padding: "28px 20px",
        textAlign: "center",
        color: T.mid,
        fontSize: T.fsSm,
        background: T.lightGray,
        borderRadius: 8,
      }}
        data-testid="hist-chart-empty"
      >
        No historical data available - capacity or badge reader not configured.
      </div>
    );
  }

  const visibleRows = rows.slice(windowStart, windowStart + WINDOW_SIZE);
  const n = visibleRows.length;

  const attendanceSeries = visibleRows.map(r =>
    Math.min(100, Math.round((parseFloat(r.avgDailyUniqueBadges) || 0) / capacity * 100))
  );
  const seatUtilSeries = visibleRows.map(r =>
    Math.min(100, Math.round((parseFloat(r.peakDailyUniqueBadges) || 0) / capacity * 100))
  );

  const attendancePeakIdx = computePeakMarkers(attendanceSeries);
  const seatUtilPeakIdx   = computePeakMarkers(seatUtilSeries);

  const attendancePoints = attendanceSeries.map((v, i) => `${svgXFn(i, n)},${svgY(v)}`).join(" ");
  const seatUtilPoints   = seatUtilSeries.map((v, i)   => `${svgXFn(i, n)},${svgY(v)}`).join(" ");

  const canPrev = windowStart > 0;
  const canNext = windowStart + WINDOW_SIZE < rows.length;

  // Y-axis gridlines at 0, 25, 50, 75, 100
  const gridlines = [0, 25, 50, 75, 100];

  return (
    <div data-testid="hist-util-chart" ref={wrapperRef}>
      {/* Range navigation (only shown when > WINDOW_SIZE months) */}
      {rows.length > WINDOW_SIZE && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, justifyContent: "flex-end" }}>
          <button
            aria-label="Previous months"
            data-testid="hist-chart-prev"
            onClick={() => setWindowStart(Math.max(0, windowStart - 1))}
            disabled={!canPrev}
            style={{
              border: `1px solid ${T.border}`,
              background: canPrev ? T.card : T.lightGray,
              color: canPrev ? T.dark : T.light,
              borderRadius: 4,
              width: 24,
              height: 24,
              cursor: canPrev ? "pointer" : "default",
              fontSize: T.fsBase,
              lineHeight: 1,
            }}
          >‹</button>
          <span style={{ fontSize: T.fsXs, color: T.mid }}>
            {monthLabel(visibleRows[0]?.month)} – {monthLabel(visibleRows[n - 1]?.month)}
          </span>
          <button
            aria-label="Next months"
            data-testid="hist-chart-next"
            onClick={() => setWindowStart(Math.min(rows.length - WINDOW_SIZE, windowStart + 1))}
            disabled={!canNext}
            style={{
              border: `1px solid ${T.border}`,
              background: canNext ? T.card : T.lightGray,
              color: canNext ? T.dark : T.light,
              borderRadius: 4,
              width: 24,
              height: 24,
              cursor: canNext ? "pointer" : "default",
              fontSize: T.fsBase,
              lineHeight: 1,
            }}
          >›</button>
        </div>
      )}

      {/* SVG Chart — viewBox matches measured container px width so scale = 1:1.
           At 1:1, fontSize values on SVG <text> elements equal their CSS-px value. */}
      <svg
        viewBox={`0 0 ${chartW} ${CHART_H_SVG}`}
        style={{ width: "100%", height: "auto", display: "block", overflow: "visible" }}
        aria-label="Historical utilization chart"
      >
        {/* Gridlines */}
        {gridlines.map(pct => (
          <g key={pct}>
            <line
              x1={PAD_LEFT} y1={svgY(pct)}
              x2={PAD_LEFT + plotW} y2={svgY(pct)}
              stroke={T.border}
              strokeWidth={0.8}
              strokeDasharray={pct === 0 ? "none" : "3,3"}
            />
            <text
              x={PAD_LEFT - 4}
              y={svgY(pct) + 4}
              textAnchor="end"
              style={{ fontSize: "11px" }}
              fill={T.mid}
            >{pct}%</text>
          </g>
        ))}

        {/* X-axis month labels */}
        {visibleRows.map((row, i) => (
          <text
            key={i}
            x={svgXFn(i, n)}
            y={CHART_H_SVG - 6}
            textAnchor="middle"
            style={{ fontSize: "11px" }}
            fill={T.mid}
          >
            {monthLabel(row.month).split(" ")[0]}
          </text>
        ))}

        {/* Attendance % polyline */}
        {n > 1 && (
          <polyline
            data-testid="attendance-line"
            points={attendancePoints}
            fill="none"
            stroke={T.blue}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {/* Seat Utilization % polyline */}
        {n > 1 && (
          <polyline
            data-testid="seat-util-line"
            points={seatUtilPoints}
            fill="none"
            stroke={T.utilGold}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {/* Data dots for every point */}
        {attendanceSeries.map((v, i) => (
          <circle
            key={`a${i}`}
            cx={svgXFn(i, n)}
            cy={svgY(v)}
            r={3}
            fill={T.blue}
          />
        ))}
        {seatUtilSeries.map((v, i) => (
          <circle
            key={`s${i}`}
            cx={svgXFn(i, n)}
            cy={svgY(v)}
            r={3}
            fill={T.utilGold}
          />
        ))}

        {/* Attendance peak marker */}
        {attendancePeakIdx != null && (
          <g data-testid="attendance-peak-marker">
            <circle
              cx={svgXFn(attendancePeakIdx, n)}
              cy={svgY(attendanceSeries[attendancePeakIdx])}
              r={6}
              fill={T.blue}
              stroke={T.card}
              strokeWidth={2}
            />
            <text
              x={svgXFn(attendancePeakIdx, n)}
              y={svgY(attendanceSeries[attendancePeakIdx]) - 10}
              textAnchor="middle"
              style={{ fontSize: "11px", fontWeight: T.fwRegular }}
              fill={T.blue}
            >
              {monthLabel(visibleRows[attendancePeakIdx]?.month).split(" ")[0]}
            </text>
          </g>
        )}

        {/* Seat utilization peak marker */}
        {seatUtilPeakIdx != null && (
          <g data-testid="seat-util-peak-marker">
            <circle
              cx={svgXFn(seatUtilPeakIdx, n)}
              cy={svgY(seatUtilSeries[seatUtilPeakIdx])}
              r={6}
              fill={T.utilGold}
              stroke={T.card}
              strokeWidth={2}
            />
            <text
              x={svgXFn(seatUtilPeakIdx, n)}
              y={svgY(seatUtilSeries[seatUtilPeakIdx]) - 10}
              textAnchor="middle"
              style={{ fontSize: "11px", fontWeight: T.fwRegular }}
              fill={T.utilGold}
            >
              {monthLabel(visibleRows[seatUtilPeakIdx]?.month).split(" ")[0]}
            </text>
          </g>
        )}
      </svg>

      {/* Legend */}
      <ViewScaffold
        testid="hist-chart-legend"
        surface="neutral"
        legendItems={[
          { label: "Attendance % (avg badges / capacity)",  indicator: { type: "line", color: T.blue     } },
          { label: "Seat Utilization % (peak badges / capacity)", indicator: { type: "line", color: T.utilGold } },
        ]}
      />
    </div>
  );
}

/**
 * NeighborhoodPressureTable — team-level pressure breakdown.
 * SEED data only — per-team API not yet available.
 * Values vary by selectedMonthIdx to demonstrate live toggle.
 */
function NeighborhoodPressureTable({ selectedMonthIdx }) {
  const teamRows = getTeamPressureRows(selectedMonthIdx);

  return (
    <div data-testid="neighborhood-pressure-table">
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--v2-line)" }}>
              {["Team", "People", "Assigned Seats", "Utilization %", "Pressure"].map(h => (
                <th
                  key={h}
                  style={{
                    padding:       "10px 14px",
                    textAlign:     h === "Team" ? "left" : "right",
                    ...EYEBROW,
                    fontSize:      T.fsXxs,
                    letterSpacing: ".07em",
                    fontWeight:    T.fwBold,
                    color:         "var(--v2-mid)",
                    whiteSpace:    "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {teamRows.map((row, i) => (
              <tr
                key={row.team}
                data-testid={`pressure-row-${i}`}
                style={{
                  borderBottom: `1px solid ${T.border}`,
                  background: i % 2 === 0 ? T.card : T.lightGray,
                }}
              >
                <td style={{ padding: "8px 10px", color: T.dark, fontWeight: T.fwSemibold }}>{row.team}</td>
                <td style={{ padding: "8px 10px", textAlign: "right", color: T.mid }}>{row.headcount}</td>
                <td style={{ padding: "8px 10px", textAlign: "right", color: T.mid }}>{row.assigned}</td>
                <td style={{ padding: "8px 10px", textAlign: "right", color: T.dark, fontWeight: T.fwSemibold }}>
                  {row.utilPct}%
                </td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>
                  <span
                    data-testid={`pressure-chip-${i}`}
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: 10,
                      fontSize: T.fsXs,
                      fontWeight: T.fwBold,
                      color: row.pressureColor,
                      background: row.pressureBg,
                    }}
                  >
                    {row.pressure}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * UtilizationPanel
 *
 * Props:
 *   location — location object with at minimum:
 *     { id, installedCapacity, maxCapacity?, utilization[], latestHeadcount? }
 */
function UtilizationListOwner({ location }) {
  if (!location) return null;

  const rows      = Array.isArray(location.utilization) ? location.utilization : [];
  const capacity  = location.installedCapacity > 0 ? location.installedCapacity : 0;
  const headcount = location.latestHeadcount ?? null;

  const lastIdx = rows.length > 0 ? rows.length - 1 : 0;
  const [selectedIdx, setSelectedIdx] = useState(lastIdx);

  const safeIdx    = rows.length > 0 ? Math.min(selectedIdx, rows.length - 1) : 0;
  const hasData    = rows.length > 0;
  const selectedRow = hasData ? rows[safeIdx] : null;

  const metrics   = computeMetrics(rows, capacity, headcount);
  const utilScore = hasData && capacity ? computeUtilScore(rows, capacity) : null;

  const canPrev = safeIdx > 0;
  const canNext = safeIdx < rows.length - 1;

  const selectedSourceLabel = selectedRow?.sourceLabel
    ?? (selectedRow?.sourceSegment
      ? (String(selectedRow.sourceSegment).toLowerCase().includes("envoy") ? "Envoy" : "Alta/Verkada")
      : selectedRow?.sourceSystem ?? null);

  return (
    <div data-testid="utilization-panel">
      <h3 style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark, marginBottom: 16 }}>
        Utilization (badges)
      </h3>
      {!hasData && (
        <div style={{ fontSize: T.fsSm, color: T.mid, marginBottom: 16 }}>
          No attendance data — reader not installed yet
        </div>
      )}

      {/* ── Month navigator (prev/next + viewing label + source system) ─── */}
      {hasData && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <button
            data-testid="month-nav-prev"
            aria-label="Previous month"
            disabled={!canPrev}
            onClick={() => setSelectedIdx(i => Math.max(0, i - 1))}
            style={{
              border: `1px solid ${T.border}`,
              background: canPrev ? T.card : T.lightGray,
              color: canPrev ? T.dark : T.light,
              borderRadius: 4,
              width: 24,
              height: 24,
              cursor: canPrev ? "pointer" : "default",
              fontSize: T.fsBase,
              lineHeight: 1,
            }}
          >‹</button>
          <span data-testid="viewing-label" style={{ fontSize: T.fsSm, color: T.dark, fontWeight: T.fwSemibold }}>
            {monthLabel(selectedRow?.month)}
          </span>
          <button
            data-testid="month-nav-next"
            aria-label="Next month"
            disabled={!canNext}
            onClick={() => setSelectedIdx(i => Math.min(rows.length - 1, i + 1))}
            style={{
              border: `1px solid ${T.border}`,
              background: canNext ? T.card : T.lightGray,
              color: canNext ? T.dark : T.light,
              borderRadius: 4,
              width: 24,
              height: 24,
              cursor: canNext ? "pointer" : "default",
              fontSize: T.fsBase,
              lineHeight: 1,
            }}
          >›</button>
          {selectedSourceLabel && (
            <span style={{ fontSize: T.fsXs, color: T.mid, marginLeft: "auto" }}>
              {selectedRow?.sourceLabel || selectedRow?.sourceSegment
                ? `Source: ${selectedSourceLabel}`
                : selectedSourceLabel}
            </span>
          )}
        </div>
      )}

      {/* ── Card 2: Bar chart ──────────────────────────────────────────── */}
      {hasData && (
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
          <MonthBarChart rows={rows} capacity={capacity || null} selectedIdx={safeIdx} onSelect={setSelectedIdx} />
        </div>
      )}

      {/* ── Card 3: Two-column sub-grid (month detail + seat assignment) ─ */}
      {hasData && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16 }}>
            <div style={{ ...EYEBROW, marginBottom: 8 }}>
              {monthLabel(selectedRow?.month)} - detail
            </div>
            {capacity ? (
              <MonthDetailBars row={selectedRow} capacity={capacity} headcount={headcount} />
            ) : (
              <div style={{ fontSize: T.fsXs, color: T.mid }}>Capacity not set</div>
            )}
          </div>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16 }}>
            <div style={{ ...EYEBROW, marginBottom: 8 }}>Seat assignment</div>
            {capacity ? (
              <WaffleChart
                installed={capacity}
                assigned={headcount?.totalCount ?? null}
              />
            ) : (
              <div style={{ fontSize: T.fsXs, color: T.mid }}>Capacity not set</div>
            )}
          </div>
        </div>
      )}

      {/* ── Card 1: Historical Utilization Line Chart ──────────────────── */}
      <div
        data-testid="hist-util-card"
        style={{
          background:   T.card,
          border:       `1px solid ${T.border}`,
          borderRadius: 14,
          padding:      "20px 20px 16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={EYEBROW}>Historical Utilization</div>
        </div>
        <HistoricalUtilChart rows={rows} capacity={capacity || null} />
      </div>

      {/* ── Card 4: Neighborhood / team pressure table ────────────────── */}
      {hasData && (
        <div style={{
          background:   T.card,
          border:       `1px solid ${T.border}`,
          borderRadius: 14,
          padding:      16,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <div style={EYEBROW}>Neighborhood / Team Pressure</div>
            <SeedBadge />
          </div>
          <NeighborhoodPressureTable selectedMonthIdx={safeIdx} />
        </div>
      )}

      {/* ── Card 5: Busiest days ──────────────────────────────────────── */}
      {hasData && (
        <div style={{
          background:   T.card,
          border:       `1px solid ${T.border}`,
          borderRadius: 14,
          padding:      16,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={EYEBROW}>Busiest days of the week</div>
            <ProposedBadge />
          </div>
          <div style={{ fontSize: T.fsXs, color: T.mid }}>
            Daily breakdown data not yet available from badge reader integration.
            This section will show Mon-Fri attendance distribution once enabled.
          </div>
        </div>
      )}
    </div>
  );
}

export default UtilizationPanel;
